import React from 'react';
import { Link } from 'react-router-dom';

import PageLayout from 'components/common/PageLayout';
import {
  API_ROUTE,
  ENTERPRISE_CONTACT_LINK,
  SUBSCRIPTION_PLANS_PAGE,
} from 'components/constants';

import styles from './segments.module.scss';

const signals = [
  {
    number: '01',
    title: 'Answers and choices',
    text: 'Build a segment around the answers people select in a poll, quiz, form or guided question.',
  },
  {
    number: '02',
    title: 'Element and card triggers',
    text: 'Create a signal when someone clicks a chosen element or reaches a specific card in the journey.',
  },
  {
    number: '03',
    title: 'Journey context',
    text: 'Keep the product and step connected to the response so downstream teams know where the signal came from.',
  },
  {
    number: '04',
    title: 'Real-time activation',
    text: 'Fire the configured marketing pixel when a condition is met, or send the event into your existing data flow.',
  },
];

const workflow = [
  {
    title: 'Design the interaction',
    text: 'Ask a focused question inside a poll, quiz, assessment, form or guided product journey.',
  },
  {
    title: 'Choose the audience rule',
    text: 'Select the answer, action, element or card that identifies the audience you want to build.',
  },
  {
    title: 'Connect the destination',
    text: 'Attach the relevant marketing pixel or route the structured response to your CRM, analytics or internal systems.',
  },
  {
    title: 'Measure and refine',
    text: 'Review engagement and completion data, then improve the question, rule or journey that creates the segment.',
  },
];

const outcomes = [
  {
    title: 'Tailor campaign messaging',
    text: 'Build audiences around expressed interests and use those signals to make campaign creative and messaging more relevant.',
  },
  {
    title: 'Qualify and route intent',
    text: 'Send a high-intent answer to the CRM or workflow that should handle the next step.',
  },
  {
    title: 'Personalize follow-up',
    text: 'Use a known preference or need to shape the next message, offer or experience the person receives.',
  },
  {
    title: 'Enrich first-party profiles',
    text: 'Add declared preferences and journey context to the customer records your teams already use.',
  },
];

const operatingModel = [
  {
    title: 'The signal starts in the experience',
    text: 'A selected answer, click or journey step creates a meaningful event with the relevant response context.',
  },
  {
    title: 'Storycards activates the route you define',
    text: 'The configured pixel fires or the event is passed into the connection selected for that workflow.',
  },
  {
    title: 'Your systems decide the next action',
    text: 'Store the response, update a profile, start a follow-up or analyze the audience under your own rules.',
  },
];

const Segments: React.FC = () => (
  <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>First-party audience segmentation</p>
            <h1>Build audiences from what people tell you.</h1>
            <p className={styles.heroText}>
              Turn answers, choices and in-journey actions into audience groups your teams can use. Define the signal
              inside each Storycards product, then activate it with a marketing pixel or send the data to your CRM,
              analytics platform and internal systems.
            </p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
              <a href={SUBSCRIPTION_PLANS_PAGE} className={styles.textButton}>
                Start creating <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>

          <div className={styles.heroPromise}>
            <span>From declared intent to activation</span>
            <div>
              <strong>Capture a meaningful signal</strong>
              <p>Use an answer, click or journey step that reveals what the person wants, needs or prefers.</p>
            </div>
            <div>
              <strong>Act in the tools you already use</strong>
              <p>Trigger a pixel or pass structured data to the systems that manage your customer journey.</p>
            </div>
          </div>
        </div>

        <div className={styles.audienceBuilder} role="group" aria-label="Example Storycards audience rule">
          <div className={styles.builderBar}>
            <span>Audience rule</span>
            <em>Active</em>
          </div>
          <div className={styles.builderBody}>
            <div className={styles.interactionPreview}>
              <span className={styles.panelLabel}>Live interaction</span>
              <p>What are you planning next?</p>
              <div className={styles.choiceList}>
                <span className={styles.selectedChoice}>
                  <i aria-hidden="true" /> Weekend trip
                </span>
                <span>
                  <i aria-hidden="true" /> Family holiday
                </span>
                <span>
                  <i aria-hidden="true" /> Business travel
                </span>
              </div>
            </div>

            <div className={styles.ruleConnector} aria-hidden="true">
              <span />
            </div>

            <div className={styles.segmentPreview}>
              <div className={styles.segmentTitle}>
                <span className={styles.panelLabel}>Audience created</span>
                <strong>Weekend travel intent</strong>
              </div>
              <dl>
                <div>
                  <dt>Signal</dt>
                  <dd>Answer equals “Weekend trip”</dd>
                </div>
                <div>
                  <dt>Context</dt>
                  <dd>Travel planner · Question 03</dd>
                </div>
                <div>
                  <dt>Activation</dt>
                  <dd>
                    <span>Marketing pixel</span>
                    <span>CRM event</span>
                    <span>Analytics</span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.statement}>
        <div className={styles.sectionInner}>
          <h2>Ask once. Use the answer across the customer journey.</h2>
          <div>
            <p>
              Polls, quizzes, assessments, forms and guided flows let people state what they want, need or prefer in
              context. The result is an explicit first-party signal, not an audience inferred only from a page view.
            </p>
            <p>
              Storycards keeps the response connected to the interaction and journey step that produced it. Marketing,
              product and data teams can use that context to tailor follow-up, route qualified interest and understand
              how each audience group behaves.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.signalSection}>
        <div className={styles.sectionInner}>
          <header className={styles.signalHeader}>
            <p className={styles.eyebrow}>Signals you can use</p>
            <h2>Create segments from moments that matter.</h2>
          </header>
          <div className={styles.signalGrid}>
            {signals.map((item) => (
              <article key={item.title}>
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.workflowSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>How it works</p>
              <h2>Move from interaction to audience in four clear steps.</h2>
            </div>
            <p>
              The audience logic lives close to the product experience, while activation happens in the marketing and
              data systems your teams already operate.
            </p>
          </header>
          <div className={styles.workflowGrid}>
            {workflow.map((item, index) => (
              <article key={item.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.outcomesSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>From audience to action</p>
              <h2>Use declared intent to make the next step more relevant.</h2>
            </div>
            <p>
              A useful segment should change what happens next. Use the signal during the journey, in a follow-up or in
              the systems that manage customer data and campaigns.
            </p>
          </header>
          <div className={styles.outcomeGrid}>
            {outcomes.map((item, index) => (
              <article key={item.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.operatingSection}>
        <div className={styles.sectionInner}>
          <header className={styles.operatingHeader}>
            <p className={styles.eyebrow}>Designed for your existing stack</p>
            <h2>Activation starts in the experience and stays under your control.</h2>
            <div>
              <p>
                Choose the interactions that matter and the destination for each signal. Your systems keep deciding how
                customer data is stored, analyzed and used.
              </p>
              <Link to={API_ROUTE} className={styles.inlineLink}>
                Explore the Storycards API <span aria-hidden="true">→</span>
              </Link>
            </div>
          </header>
          <div className={styles.operatingGrid}>
            {operatingModel.map((item, index) => (
              <article key={item.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2>Build audiences your teams can act on.</h2>
          <div>
            <p>
              Tell us which signals you want to capture and where they need to go. We will help you shape the
              interaction, audience logic and activation path.
            </p>
            <div className={styles.ctaActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
              <a href={SUBSCRIPTION_PLANS_PAGE} className={styles.textButton}>
                Start creating <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  </PageLayout>
);

export default Segments;
